import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { mainSiteUrl } from "@/mocks/categories";

const siteUrl = "https://sugargoosheet.casa";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Sugargoo Coupon Codes & Discount Guide 2026: How to Save Money",
  description: "Find the latest Sugargoo coupon codes, discount strategies, promo deals, and money-saving tips. Updated regularly with working codes for 2026.",
  image: "https://readdy.ai/api/search-image?query=Stylish%20shopping%20discount%20concept%20photography%20with%20fashion%20accessories%20sunglasses%20scarf%20and%20designer%20wallet%20next%20to%20coupon%20tags%20on%20minimal%20pastel%20pink%20background%20soft%20natural%20lighting%20elegant%20editorial%20style%20clean%20composition&width=1400&height=700&seq=sugargoo-coupon-guide&orientation=landscape",
  datePublished: "2026-08-09",
  dateModified: "2026-08-09",
  author: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  publisher: { "@type": "Organization", name: "Sugargoo Spreadsheet", url: siteUrl },
  inLanguage: "en",
  isPartOf: { "@type": "WebSite", name: "Sugargoo Spreadsheet", url: siteUrl },
};

export default function CouponGuide() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">Sugargoo Coupon &amp; Discount Guide</span>
            </nav>

            <header className="mb-10">
              <div className="inline-block text-xs font-medium text-accent-500 bg-accent-50 px-3 py-1 rounded-full mb-4">Savings</div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                Sugargoo Coupon Codes &amp; Discount Guide 2026: How to Save Money
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-2xl">
                Find the latest Sugargoo coupon codes, discount strategies, promo deals, and money-saving tips. Updated regularly with working codes for 2026.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-400">
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i> Updated: August 2026</span>
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i> 6 min read</span>
              </div>
            </header>

            <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img src="https://readdy.ai/api/search-image?query=Stylish%20shopping%20discount%20concept%20photography%20with%20fashion%20accessories%20sunglasses%20scarf%20and%20designer%20wallet%20next%20to%20coupon%20tags%20on%20minimal%20pastel%20pink%20background%20soft%20natural%20lighting%20elegant%20editorial%20style%20clean%20composition&width=1400&height=700&seq=sugargoo-coupon-guide&orientation=landscape" alt="Sugargoo Coupon Codes and Discount Guide 2026" className="w-full h-full object-cover" />
            </div>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">How to Save on Sugargoo</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                While Sugargoo already offers competitive pricing compared to other China shopping agents, smart users can save even more by combining multiple discount strategies. Whether you are a dropshipper using the <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Sugargoo Spreadsheet</Link> for product research or a casual buyer, these money-saving techniques can reduce your total costs by 10-30%.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Types of Sugargoo Discounts</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {[
                  { icon: "ri-coupon-line", title: "Welcome Coupons", desc: "New users typically receive a welcome discount on their first order. These can range from shipping discounts to percentage-off coupons on service fees." },
                  { icon: "ri-gift-line", title: "Seasonal Promotions", desc: "Major shopping events like 11.11 (Singles Day), Black Friday, and Chinese New Year bring significant discounts across all services." },
                  { icon: "ri-user-star-line", title: "VIP Member Benefits", desc: "Higher membership tiers unlock better exchange rates, lower service fees, and exclusive shipping discounts for frequent users." },
                  { icon: "ri-share-line", title: "Referral Programs", desc: "Referring friends to Sugargoo earns both you and your referral credits toward future purchases and shipping." },
                  { icon: "ri-money-dollar-circle-line", title: "Shipping Coupons", desc: "Periodic shipping discount coupons that reduce international shipping costs by a fixed amount or percentage." },
                  { icon: "ri-bank-card-line", title: "Payment Method Discounts", desc: "Some payment methods offer cashback or reduced processing fees when used on the Sugargoo platform." },
                ].map((d, i) => (
                  <div key={i} className="flex gap-3 p-4 bg-background-100 border border-background-200/70 rounded-lg">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-50">
                      <i className={`${d.icon} text-accent-500 text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{d.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{d.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Money-Saving Strategies for 2026</h2>
              <div className="space-y-3">
                {[
                  "Combine multiple items into one shipment to reduce per-item shipping costs significantly.",
                  "Time your purchases around major sales events when Sugargoo offers platform-wide discounts.",
                  "Use the DIY ordering system instead of the assisted purchase service to avoid additional service fees.",
                  "Check the Sugargoo announcement page regularly for flash deals and limited-time coupon codes.",
                  "Build a long-term relationship with consistent order volume to qualify for better VIP rates.",
                  "Compare shipping carriers before finalizing — sometimes the seemingly more expensive option is actually cheaper for your specific package dimensions.",
                ].map((tip, i) => (
                  <div key={i} className="flex gap-3 text-sm text-foreground-600">
                    <i className="ri-check-fill text-primary-500 flex-shrink-0 mt-0.5"></i>
                    <span className="leading-relaxed">{tip}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">FAQ: Sugargoo Discounts</h2>
              <div className="space-y-3">
                {[
                  { q: "Where can I find the latest Sugargoo coupon codes?", a: "Check the official Sugargoo announcements page, their social media channels, and community forums. Coupon codes are typically released during promotional events and holidays." },
                  { q: "Do Sugargoo coupons stack?", a: "Generally, only one coupon can be used per order. However, membership discounts and payment method cashback can often be combined with coupon codes for additional savings." },
                  { q: "How does the Sugargoo referral program work?", a: "Share your unique referral link with friends. When they sign up and place their first order, both you and your friend receive account credits that can be applied to future purchases." },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-100 border border-background-200/70 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4"><p className="text-xs text-foreground-600 leading-relaxed">{faq.a}</p></div>
                  </details>
                ))}
              </div>
            </section>

            <div className="bg-background-900 rounded-2xl p-8 md:p-10 text-center mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">Start Saving on Sugargoo Today</h2>
              <p className="text-sm text-background-400 max-w-md mx-auto mb-6">Combine the Sugargoo Spreadsheet with smart discount strategies for maximum savings on every order.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-900 bg-primary-400 hover:bg-primary-300 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer">Get Sugargoo Spreadsheet</a>
                <Link to="/" className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-lg transition-all duration-200 whitespace-nowrap">Back to Homepage</Link>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Related Resources</h3>
              <ul className="space-y-1.5">
                <li><Link to="/blog/sugargoo-review-2026" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Review 2026 — Complete Platform Analysis</Link></li>
                <li><Link to="/blog/sugargoo-shipping-cost-guide" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Shipping Cost Guide — Cheapest Methods</Link></li>
                <li><Link to="/" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">Sugargoo Spreadsheet Homepage — Browse All Resources</Link></li>
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}